import React, { useState } from 'react';
import {
    DeleteSvg,
    MasterCardSvg,
    PlusIcon,
    VisaCardSvg,
} from './svgs';
import AddPaymentModal from './AddPaymentModal';
import { useSelector, useDispatch } from 'react-redux';
import {
    deletePaymentMethod,
    setPrimaryOrBackupPaymentMethod,
} from '../../redux/users/actionCreator';
import { Spin } from 'antd';

const PaymentMethods = () => {
    const dispatch = useDispatch();

    const {
        userData,
        paymentMethodDeleteLoadingCardId,
        paymentMethodRoleLoadingCardId,
        paymentMethodRoleLoadingType,
    } = useSelector((state) => state.user);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const cards = Array.isArray(userData?.cards) ? userData.cards : [];
    const hasPrimary = cards.some((card) => Boolean(card?.isPrimary));
    const isSingleCard = cards.length === 1;

    const toggleModal = () => {
        setIsModalVisible(!isModalVisible);
    };

    const handleDelete = async (card) => {
        const remainingCards = cards.filter((item) => item.id !== card.id);
        const shouldPromoteReplacement = Boolean(card?.isPrimary) && remainingCards.length > 0;
        const nextPrimaryCard =
            remainingCards.find((item) => Boolean(item?.isBackup)) ||
            remainingCards[0] ||
            null;

        try {
            await dispatch(deletePaymentMethod(card.id));

            if (shouldPromoteReplacement && nextPrimaryCard?.id) {
                await dispatch(
                    setPrimaryOrBackupPaymentMethod(nextPrimaryCard.id, 'primary')
                );
            }
        } catch (error) {
            // Error is already handled by toast in actionCreator
        }
    };

    const handleSetRole = async (cardId, type) => {
        try {
            await dispatch(setPrimaryOrBackupPaymentMethod(cardId, type));
        } catch (error) {
            // Error is already handled by toast in actionCreator
        }
    };

    const getActionType = (card) => {
        if (card?.isPrimary || card?.isBackup) return null;
        if (isSingleCard) return null;
        if (!hasPrimary) return 'primary';
        return 'backup';
    };

    const renderBadge = (card) => {
        if (card?.isPrimary || isSingleCard) {
            return (
                <span className="payment-method-badge payment-method-badge--primary">
                    Primary
                </span>
            );
        }

        if (card?.isBackup) {
            return (
                <span className="payment-method-badge payment-method-badge--backup">
                    Backup
                </span>
            );
        }

        return null;
    };

    return (
        <div>
            <div className="d-flex justify-content-lg-between w-100 pt-4">
                <div className="d-flex justify-content-lg-between w-100 flex-column flex-lg-row gap-4 gap-lg-0 align-items-lg-start">
                    <div>
                        <p className="m-0 poppins f-16 lh-20 c-0f151d fw-semibold">
                            My payment methods
                        </p>
                        <p className="m-0 poppins lh-20 c-667085 pt-2">
                            Securely save your card details for hassle-free
                            payments.
                        </p>
                    </div>
                    <div>
                        <button
                            className="d-flex align-items-center gap-1.5 rounded-3"
                            onClick={toggleModal}
                            style={{
                                border: '1.5px solid #2b5c35',
                                background: 'transparent',
                                color: '#2b5c35',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                padding: '4px 10px',
                                height: '32px'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#e8f4ec';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', transform: 'scale(0.8)', transformOrigin: 'center' }}>
                                <PlusIcon />
                            </div>
                            <div className="poppins f-13 fw-semibold" style={{ color: '#2b5c35', whiteSpace: 'nowrap' }}>
                                Add payment method
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            <div className="row mt-3 pb-3">
                {cards.map((item) => {
                    const actionType = getActionType(item);
                    const isRoleLoading =
                        paymentMethodRoleLoadingCardId === item.id &&
                        Boolean(paymentMethodRoleLoadingType);
                    const isDeleting =
                        paymentMethodDeleteLoadingCardId === item.id;

                    return (
                        <div className="col-sm-6 col-12 p-2" key={item.id}>
                            <div 
                                className="border py-3 px-3 d-flex flex-column bg-white"
                                style={{
                                    borderRadius: '12px',
                                    borderColor: '#e4e7ec',
                                    boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                                    minHeight: '112px',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        {(item?.card?.brand || item?.brand) === 'visa' ? (
                                            <VisaCardSvg />
                                        ) : (item?.card?.brand || item?.brand) === 'mastercard' ? (
                                            <MasterCardSvg />
                                        ) : null}
                                        {renderBadge(item)}
                                    </div>

                                    <div className="d-flex align-items-center gap-2 ms-auto">
                                        {actionType ? (
                                            <button
                                                type="button"
                                                className={`btn btn-sm py-1 px-2.5 poppins fw-semibold rounded-2 transition-all ${
                                                    actionType === 'primary'
                                                        ? 'btn-success'
                                                        : 'btn-dark'
                                                }`}
                                                style={{
                                                    fontSize: '11px',
                                                    padding: '3px 8px',
                                                    border: 'none',
                                                    boxShadow: 'none'
                                                }}
                                                disabled={isRoleLoading}
                                                onClick={() =>
                                                    handleSetRole(
                                                        item.id,
                                                        actionType
                                                    )
                                                }
                                            >
                                                {isRoleLoading
                                                    ? 'Saving...'
                                                    : actionType === 'primary'
                                                    ? 'Set Primary'
                                                    : 'Set Backup'}
                                            </button>
                                        ) : null}

                                        <button
                                            type="button"
                                            className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-1.5 border-0 bg-transparent"
                                            style={{ 
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fef3f2'}
                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            onClick={() => handleDelete(item)}
                                        >
                                            {isDeleting ? (
                                                <Spin size="small" />
                                            ) : (
                                                <DeleteSvg />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between align-items-center pt-2">
                                    <p className="mb-0 poppins f-15 fw-semibold text-dark" style={{ letterSpacing: '1px' }}>
                                        •••• •••• •••• {item?.card?.last4 || item?.last4}
                                    </p>
                                    <p className="mb-0 poppins f-12 fw-medium" style={{ color: '#667085' }}>
                                        Exp: {item?.card?.exp_month || item?.exp_month}/{item?.card?.exp_year || item?.exp_year}
                                    </p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <AddPaymentModal
                isModalVisible={isModalVisible}
                handleClose={toggleModal}
            />
        </div>
    );
};

export default PaymentMethods;
